import type { ModelInfo } from '@paradigm/terminal-core';

import { formatVersionLabel, parseModelId } from './provider-groups';

/**
 * Friendly label for a model id in the
 * `<openclaw-provider>/provider/company/model/version` format (first segment
 * = the gateway's routing provider: `dtmodel`, `dt-a`, `dt-o`, …). Derives
 * the display name directly from the structured id segments — no static
 * lookup table needed.
 *
 * Examples:
 *   `dt-a/bedrock/anthropic/opus/4.8`      → `Opus 4.8`
 *   `dtmodel/bedrock/deepseek/deepseek-v3/` → `DeepSeek V3`
 *   `dt-a/qwen/qwen/qwen-3.7/max`          → `Qwen 3.7 Max`
 */
export function defaultModelLabel(id: string): string {
  const parsed = parseModelId(id);
  // If parsing yields a model segment, derive the label from it.
  if (parsed.model.length > 0) {
    return formatVersionLabel(parsed);
  }
  // Unparseable → return the raw id.
  return id;
}

/**
 * Normalize a model id for comparison: strip the openclaw routing provider
 * and the LiteLLM provider so `dt-a/bedrock/anthropic/opus/4.8` and
 * `dtmodel/fireworks-ai/anthropic/opus/4.8` compare equal. Also handles the
 * legacy `[region.]provider.model` format and the `amazon-bedrock/` prefix
 * for backward compatibility.
 *
 * Stripping the openclaw prefix positionally is what lets a pick persisted
 * as `dtmodel/bedrock/anthropic/opus/4.8` reconcile-match the renamed
 * catalog id `dt-a/bedrock/anthropic/opus/4.8` — both normalize to
 * `anthropic/opus/4.8` — without enumerating past or future prefix names.
 */
export function normalizeModelId(id: string): string {
  if (id.includes('/')) {
    // Strip the openclaw routing provider (first segment — `dtmodel[-oai]`,
    // `dt-a`/`dt-o`, config-defined), then the LiteLLM provider (next
    // segment: bedrock, fireworks-ai, etc.) so the same model from different
    // providers compares equal: company/model/version. Legacy single-prefix
    // ids (`amazon-bedrock/…`) simply run out of slashes after one strip.
    let rest = id;
    for (let i = 0; i < 2; i++) {
      const slash = rest.indexOf('/');
      if (slash <= 0) break;
      rest = rest.slice(slash + 1);
    }
    return rest.replace(/\/(?:DEFAULT|default)$/u, '').replace(/\/$/u, '');
  }
  // Legacy format: strip amazon-bedrock/ prefix and two-letter region.
  return id.replace(/^amazon-bedrock\//u, '').replace(/^[a-z]{2}\./u, '');
}

/**
 * Find `id` in the live catalog: exact id first, else a region/provider-
 * agnostic match (so a pick stored as `dtmodel/bedrock/anthropic/opus/4.8`
 * still resolves to the renamed `dt-a/...` entry). `undefined` when the
 * catalog carries no equivalent.
 *
 * The single home for this matching. Callers that need a display id and
 * callers that need a label must agree on what counts as "the same model" —
 * when they disagreed, state named one model and the picker rendered
 * another.
 */
export function resolveModelInCatalog(
  models: readonly ModelInfo[],
  id: string | null | undefined,
): ModelInfo | undefined {
  if (id == null) return undefined;
  const exact = models.find((m) => m.id === id);
  if (exact != null) return exact;
  // Hoisted: normalizing the needle per candidate re-ran two regexes for
  // every catalog entry.
  const needle = normalizeModelId(id);
  return models.find((m) => normalizeModelId(m.id) === needle);
}

/** True when two ids denote the same model, ignoring routing/region prefixes. */
export function modelIdsMatch(a: string, b: string): boolean {
  // Exact equality is subsumed: normalizeModelId is a pure function of the id.
  return normalizeModelId(a) === normalizeModelId(b);
}

/**
 * The model to pre-select when the user hasn't picked one — Opus, from either
 * source. Prefers a non-legacy Opus explicitly, then falls back to the first
 * non-legacy model, then anything. `null` only when the list is empty.
 */
export function pickDefaultModelId(
  models: readonly ModelInfo[],
): string | null {
  const opus = models.find(
    (m) => m.legacy !== true && normalizeModelId(m.id).includes('opus'),
  );
  if (opus != null) return opus.id;
  const nonLegacy = models.find((m) => m.legacy !== true);
  return (nonLegacy ?? models[0])?.id ?? null;
}
